import { type RoleModel } from '@/types';
import { convertToTranslationKey, processSpecialCharacters } from '@/utils/stringHelpers';
import type { TranslationKeys } from '@/utils/translations';
import { useTranslations } from '@/utils/translations';
import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

/**
 * Specialties added after `src/locales/en/common.json` was frozen against new
 * keys (see scripts/check-common-json-additions.ts) have no legacy `specialties.*`
 * entry, so `t()` echoes the lookup key back verbatim. `getTranslatedSpecialty`
 * detects that and falls back to one of these react-intl messages instead of
 * showing the raw "specialties.X" key. Keyed by the same PascalCase key
 * `getSpecialtyTranslationKey` produces for the specialty name.
 */
const missingSpecialtyMessages = defineMessages({
  Biokineticist: { defaultMessage: 'Biokineticist', id: 'MGhHdJEuYV' },
  RuralGeneralist: { defaultMessage: 'Rural Generalist', id: 'i8sc43o66D' },
  ClinicalCounsellor: { defaultMessage: 'Clinical Counsellor', id: 'jW5/WR++Ud' },
  PsychiatricMentalHealthNursePractitionerPMHNP: {
    defaultMessage: 'Psychiatric Mental Health Nurse Practitioner (PMHNP)',
    id: 'rxFTPIK5ol',
  },
  EquineVeterinarian: { defaultMessage: 'Equine Veterinarian', id: 'K2Yf7L1ewv' },
});

/**
 * Custom hook that provides functions to translate role names and specialties
 * @returns Object containing translation helper functions
 */
export const useTranslationHelpers = () => {
  const { t, locale, i18n } = useTranslations();
  const intl = useIntl();
  // The language that actually drives `t()` output. Equals `locale` in the
  // normal app (TranslationsProvider keeps them in sync), but diverges under the
  // dictate-onboarding scoped i18next instance, where `t` follows the OS/saved
  // language while the router-derived `locale` stays `en`. Cache keys and
  // sorting must follow this, not `locale`.
  const activeLanguage = i18n?.language ?? locale;

  /**
   * Converts a role name to a translation key format
   */
  const getRoleTranslationKey = useCallback((name: string): string => {
    // Type safety check
    if (typeof name !== 'string') {
      return '';
    }

    const roleKeyMap: Record<string, string> = {
      'Account Owner': 'AccountOwner',
      Administrator: 'Administrator',
      Assistant: 'Assistant',
      Clinician: 'Clinician',
      Coder: 'Coder',
      Compliance: 'Compliance',
      'Dictate Only': 'DictateOnly',
    };

    if (roleKeyMap[name]) {
      return roleKeyMap[name];
    }

    return name
      .split(/\s+/)
      .map((word: string) => {
        // Add explicit type assertion for word
        return typeof word === 'string' && word.length > 0
          ? word.charAt(0).toUpperCase() + word.slice(1)
          : '';
      })
      .join('')
      .replace(/[^a-zA-Z0-9]/g, '');
  }, []);

  /**
   * Converts a specialty name to a translation key format
   */
  const getSpecialtyTranslationKey = useCallback((name: string): string => {
    const processedName = processSpecialCharacters(name);
    return convertToTranslationKey(processedName);
  }, []);

  /**
   * Converts a note template name to a translation key format
   */
  const getNoteTemplateTranslationKey = useCallback((name: string): string => {
    // Type safety check
    if (typeof name !== 'string') {
      return '';
    }

    const templateKeyMap: Record<string, string> = {
      'H & P': 'HAndP',
      'H & P (Issues)': 'HAndPIssues',
      SOAP: 'SOAP',
      'SOAP (Issues)': 'SOAPIssues',
      'Issues List': 'IssuesList',
      'Referral letter': 'ReferralLetter',
      'Mental health note': 'MentalHealthNote',
      'Skin check note': 'SkinCheckNote',
      'GP MP/TCA': 'GPMPTCA',
      'Physio note': 'PhysioNote',
      'OT note': 'OTNote',
      'Psychotherapy note': 'PsychotherapyNote',
      'SMART goals': 'SMARTGoals',
      '6-week infant check': 'SixWeekInfantCheck',
      'Work cover form': 'WorkCoverForm',
      'Iron infusion consent': 'IronInfusionConsent',
      'Mirena insertion consent': 'MirenaInsertionConsent',
      'ED admission note': 'EDAdmissionNote',
      'ED discharge summary': 'EDDischargeSummary',
    };

    if (templateKeyMap[name]) {
      return templateKeyMap[name];
    }

    // If not in the map, use the same transformation as specialties
    const processedName = processSpecialCharacters(name);
    return convertToTranslationKey(processedName);
  }, []);

  const getTranslatedRoleName = useCallback(
    (roleName: string): string => {
      const roleKey = getRoleTranslationKey(roleName);
      // we need to cast to TranslationKeys because t() expects a valid key
      const translationKey = `teamRoles.${roleKey}` as TranslationKeys;
      const translated = t(translationKey);
      return translated === translationKey ? roleName : translated;
    },
    [t, getRoleTranslationKey]
  );

  const getTranslatedSpecialty = useCallback(
    (specialty: string): string => {
      if (!specialty) return t('common.notSpecified');
      const specialtyKey = getSpecialtyTranslationKey(specialty);
      // we need to cast to TranslationKeys because t() expects a valid key
      const translationKey = `specialties.${specialtyKey}` as TranslationKeys;
      const translated = t(translationKey);
      if (translated !== translationKey) return translated;

      const fallbackMessage =
        missingSpecialtyMessages[specialtyKey as keyof typeof missingSpecialtyMessages];
      return fallbackMessage ? intl.formatMessage(fallbackMessage) : translated;
    },
    [t, intl, getSpecialtyTranslationKey]
  );

  /**
   * Translates a note template name using the translation system
   */
  const getTranslatedNoteTemplate = useCallback(
    (templateName: string): string => {
      if (!templateName) return t('common.notSpecified');
      const templateKey = getNoteTemplateTranslationKey(templateName);
      // we need to cast to TranslationKeys because t() expects a valid key
      const translationKey = `noteTemplates.${templateKey}` as TranslationKeys;
      const translated = t(translationKey);

      // If the translation is the same as the key (not found), return the original value
      return translationKey === translated ? templateName : translated;
    },
    [t, getNoteTemplateTranslationKey]
  );

  /**
   * Transforms a list of role objects by translating their names and descriptions
   */
  const translateRoles = useCallback(
    (roles: RoleModel[]) => {
      return roles.map((role) => {
        const roleKey = getRoleTranslationKey(role.name);
        // we need to cast to TranslationKeys because t() expects a valid key
        const nameKey = `teamRoles.${roleKey}` as TranslationKeys;
        const translatedName = t(nameKey);

        const translatedDesc = role.desc ? { ...role.desc } : null;

        if (translatedDesc && translatedDesc.summary) {
          const summaryKey = `teamRoles.${roleKey}Desc` as TranslationKeys;
          const translatedSummary = t(summaryKey);
          translatedDesc.summary =
            translatedSummary === summaryKey ? translatedDesc.summary : translatedSummary;
        }

        if (translatedDesc && translatedDesc.able && Array.isArray(translatedDesc.able)) {
          translatedDesc.able = translatedDesc.able.map(
            (permission: string, index: number) => {
              const permissionKey = `teamRoles.${roleKey}Able${index}`;
              const translatedPermission = t(permissionKey as TranslationKeys);
              return translatedPermission === permissionKey
                ? permission
                : translatedPermission;
            }
          );
        }

        if (
          translatedDesc &&
          translatedDesc.disable &&
          Array.isArray(translatedDesc.disable)
        ) {
          translatedDesc.disable = translatedDesc.disable.map(
            (restriction: string, index: number) => {
              const restrictionKey = `teamRoles.${roleKey}Disable${index}`;
              // we need to cast to TranslationKeys because t() expects a valid key
              const translatedRestriction = t(restrictionKey as TranslationKeys);
              return translatedRestriction === restrictionKey
                ? restriction
                : translatedRestriction;
            }
          );
        }

        return {
          ...role,
          name: translatedName === nameKey ? role.name : translatedName,
          desc: translatedDesc,
        };
      });
    },
    [t, getRoleTranslationKey]
  );

  /**
   * Transforms a list of note template objects by translating their names
   */
  const translateNoteTemplates = useCallback(
    <T extends { name: string }>(templates: T[]): T[] => {
      return templates.map((template) => {
        return {
          ...template,
          name: getTranslatedNoteTemplate(template.name),
        };
      });
    },
    [getTranslatedNoteTemplate]
  );

  return {
    /** Resolved app locale from account/router (e.g. `es-ES`). */
    locale,
    /** Language that actually drives `t()` output. Use this for caches/sorting —
     *  it tracks the scoped onboarding i18next instance, unlike `locale`. */
    activeLanguage,
    getRoleTranslationKey,
    getSpecialtyTranslationKey,
    getNoteTemplateTranslationKey,
    getTranslatedRoleName,
    getTranslatedSpecialty,
    getTranslatedNoteTemplate,
    translateRoles,
    translateNoteTemplates,
  };
};
